import type moment from "moment"
import { Instruction } from "@/models/instruction.model"
import { InstructionRepository } from "@/repositories/instruction-repository"
import { type IInstruction } from "@/interfaces/api/instruction.interface"
import { GenericModelController } from "@/controllers/model-controllers/abstract/generic-model-controller"
import { type AxiosResponse } from "axios"
import { InstructionApiExceptionIds } from "@/enums/exceptions/instruction-api-exception-ids"
import { NoReassignWithFeedbackException } from "@/exceptions/validation/instruction/no_reassign_with_feedback.exception.ts"
import { type IRepositoryRequestConfig } from "@/interfaces/repository/repository-request-config.interface"
import OrderController from "@/controllers/order-controller"
import { useOrderStore } from "@/store/orders.store"
import { storeToRefs } from "pinia"
import { useRelatedObjectsManager } from "@/composables/socket/related-objects-manager"
import { Order } from "@/models/order.model"
import { useSocketSynchronizationHandlers } from "@/composables/socket/socket-synchronization-handlers"

export default class InstructionController extends GenericModelController<
  Instruction,
  IInstruction,
  InstructionRepository
> {
  constructor(orderController: OrderController) {
    const orderStore = useOrderStore()
    const { orders } = storeToRefs(orderStore)

    const { pushToRelatedObjects, removeFromRelatedObjects } = useRelatedObjectsManager(
      orders,
      (instruction: Instruction) => instruction.order,
      (order: Order) => order.id!,
      (order: Order) => order.instructions,
      orderController.retrieveMissingOrder.bind(orderController)
    )

    const storeFunctionsConfig = {
      onCreate: pushToRelatedObjects,
      onDelete: removeFromRelatedObjects
    }

    useSocketSynchronizationHandlers(
      Instruction,
      null,
      pushToRelatedObjects,
      removeFromRelatedObjects
    )

    super(Instruction, storeFunctionsConfig, new InstructionRepository())
  }

  public async create(
    instance: Instruction,
    requestConfig?: IRepositoryRequestConfig,
    override = false
  ): Promise<Instruction | undefined> {
    instance.normalizeDateTimes()
    return await super.create(instance, requestConfig, override)
  }

  public async update(instance: Instruction): Promise<Instruction | undefined> {
    instance.normalizeDateTimes()

    const statusCodeHandlers = [
      {
        statusCode: 400,
        callback: (response: AxiosResponse) => {
          if (response.data.exceptionId === InstructionApiExceptionIds.NO_REASSIGN_WITH_FEEDBACK) {
            new NoReassignWithFeedbackException().throw()
          }
          return false
        }
      },
      {
        statusCode: 423,
        callback: (response: AxiosResponse) => {
          const error = new Error("Received 423 status code")
          ;(error as any).cause = response
          throw error
        }
      }
    ]
    const updatedModel = await super.update(instance, { statusCodeHandlers })
    if (updatedModel) {
      instance.lastChangeRelevantToServiceProviderAt =
        updatedModel.lastChangeRelevantToServiceProviderAt
    }
    return updatedModel
  }

  /**
   * Update the instruction, but rollback to the original instance if the update fails.
   * @param instance The updated instance.
   * @param originalInstance The original instance.
   * TODO: See if the base model controller should support this
   */
  public async updateOrRollback(
    instance: Instruction,
    originalInstance: Instruction
  ): Promise<Instruction | undefined> {
    try {
      return await this.update(instance)
    } catch (e) {
      console.log(e)
      Object.assign(instance, originalInstance)
    }
    return instance
  }

  public async generateSummaryTextSuggestion(instruction: Instruction) {
    return this.apiFunctions.getRepository().generateSummaryTextSuggestion(instruction)
  }

  /**
   * Ask the backend to clone the given instructions to the given target date.
   * Created/updated instructions are synchronized back to the client via websockets,
   * so we don't have to manipulate the store ourselves here.
   */
  public async cloneToDate(instructions: Instruction | Instruction[], targetDate: moment.Moment) {
    const instructionList = Array.isArray(instructions) ? instructions : [instructions]
    const instructionIds = instructionList.map((instruction) => instruction.id!)
    if (!instructionIds.length) {
      throw new Error("No instructions provided for cloning.")
    }
    return this.apiFunctions.getRepository().cloneToDate(instructionIds, targetDate)
  }
}
